import { ReactNode, forwardRef, type Ref } from 'react';
import { cn } from '@/lib/utils';

interface InfoPanelProps {
    children: ReactNode;
    className?: string;
}

interface InfoPanelGroupProps {
    children: ReactNode;
    className?: string;
}

type InfoPanelCardBaseProps = {
    children: ReactNode;
    className?: string;
    isInteractive?: boolean;
    isActive?: boolean;
};

type InfoPanelCardButtonProps = {
    as: 'button';
    type?: 'button' | 'submit' | 'reset';
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'className'>;

type InfoPanelCardDivProps = {
    as?: 'div';
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'className'>;

type InfoPanelCardProps = InfoPanelCardBaseProps &
    (InfoPanelCardButtonProps | InfoPanelCardDivProps);

interface InfoPanelTitleProps {
    children: ReactNode;
    className?: string;
}

interface InfoPanelRowProps {
    children: ReactNode;
    className?: string;
}

interface InfoPanelValueProps {
    children: ReactNode;
    className?: string;
}

export function InfoPanel({ children, className }: InfoPanelProps) {
    return (
        <div
            className={cn(
                'flex w-full flex-col items-stretch divide-y divide-grey-500 min-w-0 @wide:flex-row @wide:divide-y-0 @wide:divide-x',
                className,
            )}
        >
            {children}
        </div>
    );
}

export function InfoPanelGroup({ children, className }: InfoPanelGroupProps) {
    return (
        <div
            className={cn(
                'flex flex-1 basis-0 w-full min-w-0 items-stretch flex-col @narrow:flex-row divide-y divide-grey-500 @narrow:divide-y-0 @narrow:divide-x @wide:w-1/2 @wide:flex-none',
                className,
            )}
        >
            {children}
        </div>
    );
}

export const InfoPanelCard = forwardRef<
    HTMLButtonElement | HTMLDivElement,
    InfoPanelCardProps
>(function InfoPanelCard(
    { children, className, isInteractive = false, isActive = false, ...rest },
    ref,
) {
    const combinedClassName = cn(
        'flex flex-1 basis-0 w-full flex-col justify-end gap-3 px-4 py-4 min-w-0',
        isInteractive &&
            'cursor-pointer hover:bg-grey-550 transition-colors text-left',
        isActive && 'bg-grey-550',
        className,
    );

    if (rest.as === 'button') {
        const { type, as: _as, ...buttonProps } = rest;
        return (
            <button
                ref={ref as Ref<HTMLButtonElement>}
                type={type ?? 'button'}
                className={combinedClassName}
                {...buttonProps}
            >
                {children}
            </button>
        );
    }

    const { as, ...divProps } = rest;
    return (
        <div
            ref={ref as Ref<HTMLDivElement>}
            className={combinedClassName}
            {...divProps}
        >
            {children}
        </div>
    );
});

export function InfoPanelTitle({ children, className }: InfoPanelTitleProps) {
    return (
        <div className={cn('text-sm-medium text-grey-200', className)}>
            {children}
        </div>
    );
}

export function InfoPanelRow({ children, className }: InfoPanelRowProps) {
    return (
        <div className={cn('flex items-center gap-2 min-w-0', className)}>
            {children}
        </div>
    );
}

export function InfoPanelValue({ children, className }: InfoPanelValueProps) {
    return (
        <span className={cn('text-sm-medium text-white-100', className)}>
            {children}
        </span>
    );
}
